import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useSignal } from "react-alien-signals";
import { useStore } from "zustand";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  LoadingIcon,
  Tip2Icon,
  GirlIcon,
  BoyIcon,
  Voice2Icon,
  InfoIcon,
  Voice3Icon,
  DragIcon,
  DelIcon,
  SuccessIcon,
  WebsiteIcon
} from "@/components/Icons";
import { WhatsappLogo } from "@/components/icons/logos";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent
} from "@/components/ui-new/tabs";
import AgentPreview from "./AgentPreview";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import api, {
  saveBrandKit,
  createWhatsappVoiceAgent,
  updateWhatsappVoiceAgent
} from "@/lib/api/client";
import { usePageStore, useUserStore, useConfigStore } from "@/stores/page";
import { type VoiceAgent, type VoiceMode, PlanType } from "@/lib/api/schema";
import { type WhatsAppVoiceAgent } from "@/types/whatsappVoiceAgent";
import { useToast } from "@/hooks/use-toast";
import { convertFieldName, convertFieldNameToKey } from "@/lib/utils";
import { initTrainingDrawerStore } from "@/stores/training";
import { useTranslation } from "react-i18next";
import useGate from "@/hooks/useGate";

export default function VoiceAgentSetting() {
  const { t } = useTranslation("translations");
  const { id } = useParams() as { id: string };
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenLoading, setIsOpenLoading] = useState(false);
  const pageStore = usePageStore();
  const voiceAgent = useStore(pageStore, (state) => state.voiceAgent);
  const whatsappVoiceAgent = useStore(
    pageStore,
    (state) => state.whatsappVoiceAgent
  );
  const setWhatsappVoiceAgent = useStore(
    pageStore,
    (state) => state.setWhatsappVoiceAgent
  );
  const userStore = useUserStore();
  const user = useStore(userStore, (state) => state.user);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const setVoiceAgent = useStore(pageStore, (state) => state.setVoiceAgent);
  const voiceModes = useStore(pageStore, (state) => state.voiceModes);
  const setVoiceModes = useStore(pageStore, (state) => state.setVoiceModes);
  const strategyFields = useMemo(
    () => voiceAgent?.strategy.fields || [],
    [voiceAgent]
  );
  const configStore = useConfigStore();
  const qualificationCriteria = useStore(
    configStore,
    (state) => state.config.qualification_config.qualification_criteria
  );
  const brandKit = useStore(configStore, (state) => state.brandKit);
  const [, setTrainingDrawerStore] = useSignal(initTrainingDrawerStore);

  const [customFiled, setCustomFiled] = useState<{
    name: string;
    is_required: boolean;
    is_selected: boolean;
    question: string;
  }>({
    name: "",
    is_required: false,
    is_selected: false,
    question: ""
  });
  const [isStrategyUpdated, setIsStrategyUpdated] = useState(false);
  const [voiceChoose, setVoiceChoose] = useState<string[]>([]);
  const supportWhatsApp = useGate("astra-whatsapp-voice");

  const agentVoiceMode = useMemo(
    () => voiceModes.find((v) => v.id === voiceAgent?.voice_mode_id),
    [voiceModes, voiceAgent]
  );

  const voiceLanguages = useMemo(
    () => [
      {
        name: t("voiceAgent.autoDetect"),
        value: "babel"
      }
      // 其他语言选项已注释掉
    ],
    [t]
  );

  const whatsappLanguages = useMemo(
    () => [
      {
        name: t("voiceAgent.autoDetect"),
        value: "babel"
      },
      {
        name: "English",
        value: "en"
      },
      {
        name: "中文（简体）",
        value: "zh"
      },
      {
        name: "Español",
        value: "es"
      }
    ],
    [t]
  );

  const whatsappVoices = useMemo(
    () => [
      {
        name: "Alloy - Neutral and balanced",
        value: "alloy"
      },
      {
        name: "Ash - Clear and precise",
        value: "ash"
      },
      {
        name: "Ballad - Melodious and smooth",
        value: "ballad"
      },
      {
        name: "Coral - Warm and friendly",
        value: "coral"
      },
      {
        name: "Echo - Resonant and deep",
        value: "echo"
      },
      {
        name: "Sage - Calm and thoughtful",
        value: "sage"
      },
      {
        name: "Shimmer - Bright and energetic",
        value: "shimmer"
      },
      {
        name: "Verse - Versatile and expressive",
        value: "verse"
      },
      {
        name: "Marin - Feminine and soft",
        value: "marin"
      },
      {
        name: "Cedar - Warm and natural",
        value: "cedar"
      }
    ],
    []
  );

  const myPlan = useMemo(
    () => user?.subscription_info?.plan || PlanType.Free,
    [user]
  );

  useEffect(() => {
    if (voiceAgent?.strategy?.fields.length === 0) {
      setVoiceAgent({
        ...(voiceAgent as VoiceAgent),
        strategy: {
          fields: [
            {
              name: t("fields.email"),
              question: t("voiceAgent.defaultFields.emailQuestion"),
              is_selected: true,
              is_required: true
            },
            {
              name: t("fields.name"),
              question: t("voiceAgent.defaultFields.nameQuestion"),
              is_selected: true,
              is_required: true
            },
            ...(qualificationCriteria || []).map((criteria) => ({
              name: criteria.name,
              question: criteria.question as string,
              is_selected: true,
              is_required: true
            }))
          ]
        }
      });
    }
  }, []);

  // 如果已经开启但 WhatsApp Voice Agent 不存在，创建一个
  useEffect(() => {
    if (voiceAgent?.is_enabled && !whatsappVoiceAgent) {
      // 自动为已开启的 voice agent 创建 WhatsApp Voice Agent
      const createWhatsapp = async () => {
        const newWhatsappAgent = await createWhatsappVoiceAgent(
          window.tenantId,
          id
        );
        if (newWhatsappAgent) {
          setWhatsappVoiceAgent(newWhatsappAgent);
        }
      };
      createWhatsapp();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceAgent?.is_enabled]);

  const addCustomField = () => {
    setVoiceAgent({
      ...(voiceAgent as VoiceAgent),
      strategy: {
        fields: [
          ...(voiceAgent?.strategy?.fields || []),
          {
            name: convertFieldNameToKey(customFiled.name),
            is_required: customFiled.is_required,
            question: customFiled.question.trim(),
            is_selected: true
          }
        ]
      }
    });
    setIsStrategyUpdated(true);
    setCustomFiled({
      name: "",
      is_required: false,
      is_selected: true,
      question: ""
    });
  };

  const { toast } = useToast();

  const handleVoiceAgent = async (
    newVal: Partial<VoiceAgent>,
    isSave = true
  ) => {
    if (isLoading || isOpenLoading) return;
    if (typeof newVal.is_enabled === "boolean") {
      setIsOpenLoading(true);
    } else {
      setIsLoading(true);
    }
    if (voiceAgent?.id) {
      await updateVoiceAgent(newVal);
    } else {
      if (isSave) {
        toast({
          description: (
            <div className="inline-flex items-center gap-x-2">
              <InfoIcon className="block" />
              <p className="flex-1 font-medium">
                {t("voiceAgent.toast.enableFirst")}
              </p>
            </div>
          ),
          variant: "warning",
          duration: 2000
        });
        return;
      }
      setIsOpenLoading(true);
      const res = await api.mapping.v1.agents[":id"].voice
        .$post({
          param: {
            id
          },
          // @ts-ignore
          json: {
            is_enabled: true,
            tenant_id: window.tenantId,
            voice_mode_id: voiceAgent?.voice_mode_id || voiceModes[0].id,
            language: voiceAgent?.language || "babel",
            strategy: voiceAgent?.strategy,
            maximum_session_time: voiceAgent?.maximum_session_time || 300,
            is_strategy_updated: true
          }
        })
        .then((res) => res.json() as Promise<VoiceAgent & { error?: string }>);
      if (res.id) {
        setVoiceAgent(res);
        const newBrandKit = { ...brandKit };
        newBrandKit.custom_fields = {
          ...newBrandKit.custom_fields,
          ["voice-agent"]: "true"
        };
        saveBrandKit(id, newBrandKit);

        // 创建 WhatsApp Voice Agent（如果不存在）
        if (!whatsappVoiceAgent) {
          const whatsappRes = await createWhatsappVoiceAgent(
            window.tenantId,
            id
          );
          if (whatsappRes) {
            setWhatsappVoiceAgent(whatsappRes);
          }
        }
      } else {
        toast({
          description: (
            <div className="inline-flex items-center gap-x-2">
              <InfoIcon className="block" />
              <p className="flex-1 font-medium">
                {res.error || t("voiceAgent.toast.createFailed")}
              </p>
            </div>
          ),
          variant: "destructive",
          duration: 2000
        });
      }
    }
    setIsLoading(false);
    setIsOpenLoading(false);
  };

  const updateVoiceAgent = async (newVoiceAgent: Partial<VoiceAgent>) => {
    const res = await api.mapping.v1.agents[":id"].voice
      .$put({
        param: {
          id
        },
        // @ts-ignore
        json: {
          is_enabled: voiceAgent?.is_enabled,
          voice_mode_id: voiceAgent?.voice_mode_id || voiceModes[0].id,
          language: voiceAgent?.language || "en",
          strategy: voiceAgent?.strategy,
          maximum_session_time: voiceAgent?.maximum_session_time || 300,
          // inactivity_check_delay: voiceAgent?.inactivity_check_delay || 30,
          // max_retries: voiceAgent?.max_retries || 3,
          // inactivity_check_message:
          //   voiceAgent?.inactivity_check_message || "Are you still there?",
          ...newVoiceAgent,
          is_strategy_updated: isStrategyUpdated
        }
      })
      .then((res) => res.json() as Promise<VoiceAgent & { error?: string }>);
    console.log(res);
    if (res.id) {
      setVoiceAgent(res);

      // 如果启用了 Voice Agent 且 WhatsApp Voice Agent 不存在，创建一个
      if (newVoiceAgent?.is_enabled && !whatsappVoiceAgent) {
        const whatsappRes = await createWhatsappVoiceAgent(window.tenantId, id);
        if (whatsappRes) {
          setWhatsappVoiceAgent(whatsappRes);
        }
      }

      // 如果 WhatsApp Voice Agent 存在，更新它
      if (whatsappVoiceAgent?.id) {
        const whatsappUpdateData: Partial<WhatsAppVoiceAgent> = {
          agent_config: {
            ...(whatsappVoiceAgent.agent_config || {}),
            language: whatsappVoiceAgent.agent_config?.language || "babel",
            voice: whatsappVoiceAgent.agent_config?.voice || "echo",
            speed: whatsappVoiceAgent.agent_config?.speed || 1
          }
        };

        const whatsappRes = await updateWhatsappVoiceAgent(
          whatsappVoiceAgent.id,
          whatsappUpdateData
        );
        if (whatsappRes) {
          setWhatsappVoiceAgent(whatsappRes);
        }
      }

      toast({
        description: (
          <div className="inline-flex items-center gap-x-2">
            <SuccessIcon className="block" />
            <p className="flex-1 text-xs font-medium">
              {t("voiceAgent.toast.savedSuccessfully")}
            </p>
          </div>
        )
      });
      setIsStrategyUpdated(false);
      console.log("voiceAgent:", voiceAgent);
      const newBrandKit = { ...brandKit };
      newBrandKit.custom_fields = {
        ...newBrandKit.custom_fields,
        ["voice-agent"]: String(newVoiceAgent?.is_enabled)
      };
      saveBrandKit(id, newBrandKit);
    } else {
      toast({
        description: (
          <div className="inline-flex items-center gap-x-2">
            <InfoIcon className="block" />
            <p className="flex-1 font-medium">
              {res.error || t("voiceAgent.toast.saveFailed")}
            </p>
          </div>
        ),
        variant: "destructive",
        duration: 2000
      });
    }
  };

  useEffect(() => {
    if (voiceModes.length === 0) {
      const voicesRes = api.mapping.v1["voice-modes"]
        .$get()
        .then((res) => res.json() as Promise<{ voice_modes: VoiceMode[] }>);
      voicesRes.then((res) => {
        if (res.voice_modes && res.voice_modes.length > 0) {
          setVoiceModes(res.voice_modes);
        }
      });
    }
  }, []);

  const playSample = async (voice: VoiceMode) => {
    if (voice.id) {
      const res = await api.mapping.v1["voice-modes"][":id"].sample.$post({
        param: {
          id: voice.id
        },
        // @ts-ignore
        json: {
          text: t("voiceAgent.sampleText"),
          language: voiceAgent?.language || "babel"
        }
      });
      if (res && res.status === 200) {
        setPlayingId(voice.id);
        // 将响应转换为 ArrayBuffer
        const arrayBuffer = await res.arrayBuffer();

        // 创建 Blob 对象
        const audioBlob = new Blob([arrayBuffer], { type: "audio/mpeg" });

        // 创建临时 URL
        const audioUrl = URL.createObjectURL(audioBlob);

        // 播放音频
        const audio = new Audio(audioUrl);
        audio.play();

        // 播放完成后清理 URL
        audio.addEventListener("ended", () => {
          URL.revokeObjectURL(audioUrl);
          setPlayingId(null);
        });

        // 发生错误时也清理 URL
        audio.addEventListener("error", () => {
          URL.revokeObjectURL(audioUrl);
          setPlayingId(null);
        });
      } else {
        const error = (await res?.json()) as { error: string };
        console.error(error);
        toast({
          variant: "destructive",
          description: (
            <div className="inline-flex items-center gap-x-2">
              <InfoIcon className="block" />
              <p className="flex-1 font-medium">{error.error}</p>
            </div>
          )
        });
      }
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  // slider helpers (keep style untouched)
  const sliderToSpeed = (sliderValue: number): number =>
    sliderValue <= 50
      ? 0.25 + (sliderValue / 50) * (1 - 0.25)
      : 1 + ((sliderValue - 50) / 50) * (4 - 1);
  const quantizeSpeed = (value: number): number =>
    Number(
      Math.min(4, Math.max(0.25, Math.round(value / 0.05) * 0.05)).toFixed(2)
    );

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const items = reorder(strategyFields, active.id, over.id);
      setVoiceAgent({
        ...(voiceAgent as VoiceAgent),
        strategy: {
          fields: items
        }
      });
    }
  };
  return (
    <div className="mr-4 ml-2 flex h-full gap-3 overflow-hidden">
      <div className="relative flex-1 overflow-hidden rounded-xl bg-white p-6">
        <div
          className={cn(
            "flex h-full flex-col gap-y-5 pb-16",
            voiceAgent?.is_enabled ? "overflow-y-auto" : "overflow-hidden"
          )}
          style={{
            scrollbarColor: "transparent transparent",
            scrollbarWidth: "none"
          }}
        >
          <div className="z-1 flex flex-col gap-y-3">
            <div className="flex shrink-0 items-center gap-4 md:col-span-3">
              <h3 className="text-2xl font-semibold text-[#403e3e]">
                {t("voiceAgent.title")}
              </h3>
              <div className="inline-flex h-7 min-h-4 items-center justify-center gap-2 rounded bg-zinc-100 p-2.5">
                <div className="justify-start text-sm leading-tight font-medium text-zinc-800">
                  {t("voiceAgent.proFeature")}
                </div>
              </div>
              <VoiceSwitch
                isLoading={isOpenLoading}
                disabled={myPlan === PlanType.Free}
                checked={voiceAgent?.is_enabled || false}
                onCheckedChange={handleVoiceAgent}
              />
            </div>
            <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
              {t("voiceAgent.description")}
            </div>
            <div
              className={cn(
                "inline-flex items-start justify-start gap-2 self-stretch rounded-md border border-[#ffd398] bg-[#fff5e7] p-3",
                myPlan === PlanType.Free ? "inline-flex" : "hidden"
              )}
            >
              <Tip2Icon className="h-3 w-3 text-[#92310a]" />
              <div className="inline-flex flex-col items-start justify-center pr-3.5">
                <div className="h-4 justify-start text-xs leading-none font-medium text-[#92310a]">
                  {t("voiceAgent.upgradeTitle")}
                </div>
                <div className="justify-start text-xs leading-none font-normal text-[#92310a]">
                  {t("voiceAgent.upgradeDesc")}
                </div>
              </div>
              <a
                href="/admin/billing"
                className={cn(
                  "ml-auto inline-flex cursor-pointer justify-center text-center text-sm leading-normal font-medium text-[#007af5]"
                )}
              >
                {t("voiceAgent.upgradeNow")}
              </a>
            </div>
          </div>

          <Tabs defaultValue="website">
            <TabsList
              className={cn("w-full", supportWhatsApp ? "flex" : "hidden")}
            >
              <TabsTrigger value="website" className="flex-1">
                <WebsiteIcon className="mr-2 h-4 w-4" />
                Website
              </TabsTrigger>
              <TabsTrigger value="whatsapp" className="flex-1">
                <WhatsappLogo size={16} className="mr-2" />
                WhatsApp
              </TabsTrigger>
            </TabsList>

            {/* Website  */}
            <TabsContent value="website" className="flex flex-col gap-y-5">
              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.voiceModelSelection")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.voiceModelDesc")}
                </div>
                <Accordion
                  type="multiple"
                  value={voiceChoose}
                  className="flex flex-col gap-4"
                >
                  <AccordionItem
                    value="voice-choose"
                    className="relative border-b-0"
                  >
                    <AccordionTrigger
                      className="hover:border-primary flex items-center justify-between border border-zinc-200 py-0 pr-4"
                      onClick={() => {
                        setVoiceChoose(
                          voiceChoose.length > 0 ? [] : ["voice-choose"]
                        );
                      }}
                    >
                      <VoiceChooseItem
                        isActive
                        {...(agentVoiceMode || voiceModes[0] || { tag: "" })}
                        play={() => playSample(agentVoiceMode || voiceModes[0])}
                        isPlaying={playingId === voiceAgent?.voice_mode_id}
                      />
                    </AccordionTrigger>
                    <AccordionContent className="absolute left-0 z-10 mt-1.5 flex max-h-[60vh] w-full flex-col gap-1 overflow-auto rounded border border-zinc-200 bg-white p-3 shadow-md">
                      {voiceModes
                        .filter(
                          (voice) => voice.id !== voiceAgent?.voice_mode_id
                        )
                        .map((voice) => (
                          <VoiceChooseItem
                            {...voice}
                            play={() => playSample(voice)}
                            isPlaying={playingId === voice.id}
                            onClick={() => {
                              setVoiceAgent({
                                ...(voiceAgent as VoiceAgent),
                                voice_mode_id: voice.id
                              });
                              setVoiceChoose([]);
                            }}
                          />
                        ))}
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>

              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.languageSupport")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.languageDesc")}
                </div>

                <Select
                  value={voiceAgent?.language}
                  onValueChange={(val) => {
                    setVoiceAgent({
                      ...(voiceAgent as VoiceAgent),
                      language: val
                    });
                  }}
                >
                  <SelectTrigger
                    className="w-full bg-white px-4 text-sm text-[#403e3e] capitalize"
                    data-testid="agent-voice-selector-trigger"
                  >
                    <SelectValue placeholder={t("voiceAgent.voiceLanguage")} />
                  </SelectTrigger>
                  <SelectContent data-testid="agent-voice-selector-content">
                    <SelectGroup>
                      {voiceLanguages.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-sm text-[#403e3e] capitalize"
                        >
                          {option.value === "en"
                            ? option.name + " (default)"
                            : option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.strategy")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.strategyDesc")}
                </div>

                <div className="flex flex-col gap-3">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onDragEnd}
                  >
                    <SortableContext
                      items={strategyFields.map((v) => v.name)}
                      strategy={verticalListSortingStrategy}
                    >
                      {strategyFields.map((item, index) => (
                        <SortableField
                          key={item.name}
                          item={item}
                          onChange={(newVal) => {
                            setVoiceAgent({
                              ...(voiceAgent as VoiceAgent),
                              ...newVal
                            });
                            setIsStrategyUpdated(true);
                          }}
                          fields={strategyFields}
                          index={index}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                  <div className="relative flex flex-1 items-center gap-2">
                    <DragIcon className="h-6 w-6 text-slate-400" />
                    <Checkbox
                      id="voice-new-field"
                      checked={customFiled.is_selected}
                      onCheckedChange={(val) =>
                        setCustomFiled({ ...customFiled, is_selected: !!val })
                      }
                    />
                    <Input
                      className="h-5 w-25 rounded-none border-none px-0 text-sm font-medium text-[#403e3e] shadow-none focus-visible:ring-0"
                      placeholder={t("voiceAgent.enterCustom")}
                      value={customFiled.name}
                      onChange={(e) =>
                        setCustomFiled({ ...customFiled, name: e.target.value })
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (
                            customFiled.question.trim() &&
                            customFiled.name.trim()
                          ) {
                            addCustomField();
                          }
                        }
                      }}
                    />
                    <Input
                      placeholder={t("voiceAgent.conversationStarter")}
                      className="mr-7.5 h-8 flex-1 rounded-md bg-white px-3 outline outline-offset-[-1px] outline-slate-200"
                      value={customFiled.question || ""}
                      onChange={(e) =>
                        setCustomFiled((prev) => ({
                          ...prev,
                          question: e.target.value
                        }))
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (
                            customFiled.question.trim() &&
                            customFiled.name.trim()
                          ) {
                            addCustomField();
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.maxSessionTime")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.maxSessionTimeDesc")}
                </div>
                <Input
                  type="number"
                  value={voiceAgent?.maximum_session_time}
                  placeholder="300 "
                  className="h-10 pl-4 text-sm font-normal text-[#403e3e]"
                  onChange={(e) => {
                    setVoiceAgent({
                      ...(voiceAgent as VoiceAgent),
                      maximum_session_time: Number(e.target.value)
                    });
                  }}
                />
              </div>
            </TabsContent>

            {/* WhatsApp */}
            <TabsContent value="whatsapp" className="flex flex-col gap-y-5">
              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.voiceModelSelection")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.voiceModelDesc")}
                </div>

                <Select
                  value={whatsappVoiceAgent?.agent_config?.voice || "echo"}
                  onValueChange={(val) => {
                    if (whatsappVoiceAgent) {
                      setWhatsappVoiceAgent({
                        ...whatsappVoiceAgent,
                        agent_config: {
                          ...whatsappVoiceAgent.agent_config,
                          voice: val
                        }
                      });
                    }
                  }}
                >
                  <SelectTrigger
                    className="w-full bg-white px-4 text-sm text-[#403e3e]"
                    data-testid="whatsapp-voice-selector-trigger"
                  >
                    <SelectValue placeholder="Select a voice" />
                  </SelectTrigger>
                  <SelectContent data-testid="whatsapp-voice-selector-content">
                    <SelectGroup>
                      {whatsappVoices.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-sm text-[#403e3e]"
                        >
                          {option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-y-3">
                <h4 className="h-6 text-base leading-tight font-medium text-[#403e3e]">
                  {t("voiceAgent.languageSupport")}
                </h4>
                <div className="h-6 text-sm leading-tight font-normal text-[#5c5c5c]">
                  {t("voiceAgent.languageDesc")}
                </div>

                <Select
                  value={whatsappVoiceAgent?.agent_config?.language || "babel"}
                  onValueChange={(val) => {
                    if (whatsappVoiceAgent) {
                      setWhatsappVoiceAgent({
                        ...whatsappVoiceAgent,
                        agent_config: {
                          ...whatsappVoiceAgent.agent_config,
                          language: val
                        }
                      });
                    }
                  }}
                >
                  <SelectTrigger
                    className="w-full bg-white px-4 text-sm text-[#403e3e] capitalize"
                    data-testid="agent-voice-selector-trigger"
                  >
                    <SelectValue placeholder={t("voiceAgent.voiceLanguage")} />
                  </SelectTrigger>
                  <SelectContent data-testid="agent-voice-selector-content">
                    <SelectGroup>
                      {whatsappLanguages.map((option) => (
                        <SelectItem
                          key={option.value}
                          value={option.value}
                          className="text-sm text-[#403e3e] capitalize"
                        >
                          {option.value === "en"
                            ? option.name + " (default)"
                            : option.name}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-y-3">
                <div className="flex flex-col gap-y-1.5">
                  <div className="flex items-center gap-3">
                    <h4 className="text-base font-medium text-[#080808]">
                      {t("voiceAgent.speechSpeed")}:
                      <span className="ml-1 text-blue-600">
                        {whatsappVoiceAgent?.agent_config?.speed || 1}x
                      </span>
                    </h4>
                  </div>
                  <div className="text-sm text-[#5c5c5c]">
                    {t("voiceAgent.speechSpeedDesc")}
                  </div>
                </div>

                <div className="flex flex-col gap-y-1.5">
                  <div className="relative">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="1"
                      value={(() => {
                        const s = whatsappVoiceAgent?.agent_config?.speed || 1;
                        return s <= 1
                          ? ((s - 0.25) / (1 - 0.25)) * 50
                          : 50 + ((s - 1) / (4 - 1)) * 50;
                      })()}
                      onChange={(e) => {
                        if (whatsappVoiceAgent) {
                          const sliderValue = Number(e.target.value);
                          const actualSpeed = sliderToSpeed(sliderValue);

                          setWhatsappVoiceAgent({
                            ...whatsappVoiceAgent,
                            agent_config: {
                              ...whatsappVoiceAgent.agent_config,
                              speed: quantizeSpeed(actualSpeed)
                            }
                          });
                        }
                      }}
                      className="slider-blue h-1 w-full cursor-pointer appearance-none rounded-lg bg-gray-200"
                      style={{
                        background: `linear-gradient(to right, #2563eb 0%, #2563eb ${(() => {
                          const currentSpeed =
                            whatsappVoiceAgent?.agent_config?.speed || 1;
                          if (currentSpeed <= 1) {
                            return ((currentSpeed - 0.25) / (1 - 0.25)) * 50;
                          } else {
                            return 50 + ((currentSpeed - 1) / (4 - 1)) * 50;
                          }
                        })()}%, #e4e4e7 ${(() => {
                          const currentSpeed =
                            whatsappVoiceAgent?.agent_config?.speed || 1;
                          if (currentSpeed <= 1) {
                            return ((currentSpeed - 0.25) / (1 - 0.25)) * 50;
                          } else {
                            return 50 + ((currentSpeed - 1) / (4 - 1)) * 50;
                          }
                        })()}%, #e4e4e7 100%)`
                      }}
                      data-testid="speech-speed-slider"
                    />
                    <style
                      dangerouslySetInnerHTML={{
                        __html: `
                        .slider-blue::-webkit-slider-thumb {
                          appearance: none;
                          height: 14px;
                          width: 14px;
                          border-radius: 50%;
                          background: #2563eb;
                          cursor: pointer;
                          border: none;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                        .slider-blue::-moz-range-thumb {
                          height: 14px;
                          width: 14px;
                          border-radius: 50%;
                          background: #2563eb;
                          cursor: pointer;
                          border: none;
                          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                        }
                        .slider-blue::-webkit-slider-thumb:hover {
                          height: 16px;
                          width: 16px;
                          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
                        }
                        .slider-blue::-moz-range-thumb:hover {
                          height: 16px;
                          width: 16px;
                          box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.1);
                        }
                        .slider-blue:focus {
                          outline: none;
                        }
                      `
                      }}
                    />
                  </div>

                  <div className="flex justify-between text-xs font-semibold text-[#71717a]">
                    <span>{t("voiceAgent.speechSpeedSlowest")}</span>
                    <span>{t("voiceAgent.speechSpeedNormal")}</span>
                    <span>{t("voiceAgent.speechSpeedFastest")}</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <div className="hidden flex-col gap-y-3">
            <div className="flex gap-x-4">
              <div className="flex flex-1 flex-col">
                <div className="text-sm font-medium text-[#403e3e]">
                  Check Delay (seconds)
                </div>
                <Input
                  type="number"
                  value={voiceAgent?.inactivity_check_delay}
                  placeholder="300 "
                  className="h-10 pl-4 text-sm font-normal text-[#403e3e]"
                  onChange={(e) => {
                    setVoiceAgent({
                      ...(voiceAgent as VoiceAgent),
                      inactivity_check_delay: Number(e.target.value)
                    });
                  }}
                />
              </div>
              <div className="flex flex-1 flex-col">
                <div className="text-sm font-medium text-[#403e3e]">
                  Max Retries
                </div>
                <Input
                  type="number"
                  value={voiceAgent?.max_retries}
                  placeholder="5"
                  className="h-10 pl-4 text-sm font-normal text-[#403e3e]"
                  onChange={(e) => {
                    setVoiceAgent({
                      ...(voiceAgent as VoiceAgent),
                      max_retries: Number(e.target.value)
                    });
                  }}
                />
              </div>
            </div>
            <div className="text-sm font-medium text-[#403e3e]">
              Inactivity Check Message
            </div>
            <Input
              value={voiceAgent?.inactivity_check_message}
              placeholder="300 "
              className="h-10 pl-4 text-sm font-normal text-[#403e3e]"
              onChange={(e) => {
                setVoiceAgent({
                  ...(voiceAgent as VoiceAgent),
                  inactivity_check_message: e.target.value
                });
              }}
            />
          </div>
        </div>
        <div className="absolute bottom-0 left-0 flex h-16 w-full items-center justify-between rounded-br-xl rounded-bl-xl bg-white px-6 py-4 shadow-[0px_-6px_24.399999618530273px_0px_rgba(0,0,0,0.05)]">
          <Button
            variant="outline"
            className="border-primary text-primary h-9 min-w-9 rounded border text-sm font-semibold"
            data-testid="agent-voice-save-button"
            onClick={() =>
              setTrainingDrawerStore((prev) => ({ ...prev, open: true }))
            }
          >
            {t("voiceAgent.addMoreSources")}
          </Button>
          <Button
            className="h-9 min-w-9 rounded bg-[#1a87f6] text-sm font-semibold"
            data-testid="agent-voice-save-button"
            onClick={() => handleVoiceAgent({})}
          >
            {isLoading ? (
              <LoadingIcon className="text-white" />
            ) : (
              t("common.save")
            )}
          </Button>
        </div>
        <div
          className={cn(
            !voiceAgent?.is_enabled
              ? "absolute top-24 left-0 w-full bg-white/10 backdrop-blur-[1.50px]"
              : "hidden"
          )}
          style={{
            height: "calc(100% - 96px)"
          }}
        ></div>
      </div>
      <AgentPreview
        inVoice={{
          isLoading: isOpenLoading,
          disabled: myPlan === PlanType.Free,
          checked: voiceAgent?.is_enabled || false,
          onCheckedChange: handleVoiceAgent
        }}
      />
    </div>
  );
}

interface VoiceChooseItemProps {
  name: string;
  gender: number;
  tag: string;
  description: string;
  isActive?: boolean;
  onClick?: () => void;
  isPlaying?: boolean;
  play?: () => void;
}

const VoiceChooseItem = ({
  name,
  gender,
  tag,
  description,
  isActive,
  onClick,
  isPlaying,
  play
}: VoiceChooseItemProps) => {
  const { t } = useTranslation("translations");

  const translatedName = t(`voiceAgent.voiceModes.${name}`, name);

  const translatedDescription = t(
    `voiceAgent.voiceModeDescriptions.${name}`,
    description
  );

  const translatedTags = tag
    .split(",")
    .map((tagItem) =>
      t(`voiceAgent.voiceTags.${tagItem.trim()}`, tagItem.trim())
    );

  return (
    <div
      className={cn(
        "flex flex-col gap-2.5 rounded-md bg-white p-4",
        isActive
          ? ""
          : "border border-zinc-200 hover:border-transparent hover:bg-[#eef4ff]"
      )}
      onClick={onClick}
    >
      <div className="inline-flex items-center justify-start gap-2.5">
        <div className="text-base leading-normal font-semibold text-black capitalize">
          {translatedName}
        </div>
        <GirlIcon
          className={cn(
            "h-4 w-4 text-[#ff84a2]",
            gender === 1 ? "inline-flex" : "hidden"
          )}
        />
        <BoyIcon
          className={cn(
            "h-4 w-4 text-[#0175EB]",
            gender === 0 ? "inline-flex" : "hidden"
          )}
        />
        {translatedTags.map((translatedTag, index) => (
          <div
            key={`${translatedTag}-${index}`}
            className="rounded bg-zinc-100 px-2 py-1 text-sm leading-tight font-normal text-zinc-800"
          >
            {translatedTag}
          </div>
        ))}
        <Voice2Icon
          className={cn(
            "h-4.5 w-4.5 cursor-pointer text-[#403E3E]",
            isPlaying ? "hidden" : "inline-flex"
          )}
          onClick={(e) => {
            e.stopPropagation();
            play?.();
          }}
        />
        <Voice3Icon
          className={cn(
            "h-4.5 w-4.5 cursor-pointer text-[#403E3E]",
            !isPlaying ? "hidden" : "inline-flex"
          )}
        />
      </div>
      <div className="text-left text-sm leading-tight font-normal text-zinc-600">
        {translatedDescription}
      </div>
    </div>
  );
};

const SortableField = ({ item, onChange, fields, index }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.name });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };
  return (
    <div
      key={item.name}
      className="relative flex w-full items-center gap-2"
      ref={setNodeRef}
      style={style}
      {...attributes}
    >
      <DragIcon
        className="h-6 w-6 cursor-grab text-slate-400 active:cursor-grabbing"
        {...listeners}
      />
      <Checkbox
        id={`voice-field-${item.name}`}
        checked={item.is_selected}
        onCheckedChange={(val) => {
          onChange({
            strategy: {
              fields: fields.map((v, key) =>
                key === index ? { ...v, is_selected: !!val } : v
              )
            }
          });
        }}
      />
      <Label
        htmlFor={`voice-field-${item.name}`}
        className="min-w-25 text-sm font-medium text-[#403e3e]"
      >
        {convertFieldName(item.name)}
      </Label>
      <Input
        placeholder={item.question}
        className="h-8 flex-1 rounded-md bg-white px-3 outline outline-offset-[-1px] outline-slate-200"
        value={item.question || ""}
        onChange={(e) => {
          onChange({
            strategy: {
              fields: fields.map((v, key) =>
                key === index ? { ...v, question: e.target.value } : v
              )
            }
          });
        }}
      />
      <DelIcon
        className="mr-2 h-4 w-4 cursor-pointer text-slate-400"
        onClick={() => {
          onChange({
            strategy: {
              fields: fields.filter((v, key) => key !== index)
            }
          });
        }}
      />
    </div>
  );
};

const reorder = (list: any[], activeid, overid) => {
  const items = Array.from(list);
  const oldIndex = items.findIndex((item) => item.name === activeid);
  const newIndex = items.findIndex((item) => item.name === overid);

  return arrayMove(items, oldIndex, newIndex);
};

export interface VoiceSwitchProps {
  isLoading: boolean;
  disabled: boolean;
  checked: boolean;
  onCheckedChange: (val: any, isSave?: boolean) => void;
}

export const VoiceSwitch = ({
  isLoading,
  disabled,
  checked,
  onCheckedChange
}: VoiceSwitchProps) => {
  const { t } = useTranslation("translations");
  return (
    <div className="inline-flex w-fit items-center gap-4 md:col-span-3">
      <Switch
        isLoading={isLoading}
        className="ml-auto cursor-pointer focus:outline-none"
        disabled={disabled}
        checked={checked || false}
        onClick={(e) => e.stopPropagation()}
        onCheckedChange={(val) => onCheckedChange({ is_enabled: val }, false)}
        data-testid="voice-agent-enable-toggle"
      />
      <span className="text-sm font-normal text-black">
        {checked ? t("voiceAgent.on") : t("voiceAgent.off")}
      </span>
    </div>
  );
};
